import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API, authFetch } from "@/App";
import {
  Target, Send, Briefcase, FileText, Play, Pause,
  Loader2, Users, Mail, TrendingUp, Clock, RefreshCw,
  ChevronDown, Copy, Check
} from "lucide-react";

const AGENTS = [
  { id: "lead_gen", name: "Lead Generation", icon: Target, color: "text-blue-500", desc: "AI prospecting — finds ideal clients" },
  { id: "outreach", name: "Outreach Agent", icon: Send, color: "text-emerald-500", desc: "Cold email sequences & follow-ups" },
  { id: "sales_prep", name: "Sales Consultant", icon: Briefcase, color: "text-purple-500", desc: "Call prep briefs & pipeline" },
  { id: "content", name: "Content Agent", icon: FileText, color: "text-amber-500", desc: "Social media posts & warm-up content" },
];

export function SalesAgentsDashboard({ open, onOpenChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [prospects, setProspects] = useState([]);
  const [outreach, setOutreach] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [content, setContent] = useState([]);
  const [logs, setLogs] = useState([]);
  const [copied, setCopied] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, prospectsRes, outreachRes, pipelineRes, contentRes, logsRes] = await Promise.all([
        authFetch(`${API}/sales-agents/status`),
        authFetch(`${API}/sales-agents/prospects`),
        authFetch(`${API}/sales-agents/outreach`),
        authFetch(`${API}/sales-agents/pipeline`),
        authFetch(`${API}/sales-agents/content`),
        authFetch(`${API}/sales-agents/logs`),
      ]);
      if (statusRes.ok) setStatus(await statusRes.json());
      if (prospectsRes.ok) setProspects((await prospectsRes.json()).prospects || []);
      if (outreachRes.ok) setOutreach((await outreachRes.json()).outreach || []);
      if (pipelineRes.ok) setPipeline((await pipelineRes.json()).deals || []);
      if (contentRes.ok) setContent((await contentRes.json()).content || []);
      if (logsRes.ok) setLogs((await logsRes.json()).logs || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) fetchData(); }, [open, fetchData]);

  const toggleAgent = async (agentId) => {
    const res = await authFetch(`${API}/sales-agents/toggle/${agentId}`, { method: "POST" });
    if (res.ok) { const d = await res.json(); toast.success(`${agentId} ${d.enabled ? "enabled" : "disabled"}`); fetchData(); }
  };

  const triggerAgent = async (agentId) => {
    setTriggering(agentId);
    try {
      const res = await authFetch(`${API}/sales-agents/trigger/${agentId}`, { method: "POST" });
      if (res.ok) { const d = await res.json(); toast.success(`${agentId}: ${d.status} — ${JSON.stringify(d).slice(0, 80)}`); fetchData(); }
      else toast.error("Agent run failed");
    } catch { toast.error("Failed"); }
    setTriggering(null);
  };

  const copyContent = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(null), 2000);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] overflow-hidden flex flex-col p-0 gap-0" data-testid="sales-agents-dashboard">
        <DialogHeader className="p-5 border-b">
          <DialogTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#2BC40E]" /> Sales Pipeline Agents
          </DialogTitle>
          <DialogDescription>Autonomous lead gen, outreach, sales prep & content creation</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>
          ) : (
            <>
              {/* Agent Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {AGENTS.map(agent => {
                  const config = status?.agents?.[agent.id] || {};
                  const enabled = config.enabled !== false;
                  return (
                    <Card key={agent.id} className={`border-neutral-200 ${!enabled ? 'opacity-50' : ''}`} data-testid={`agent-card-${agent.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <agent.icon className={`w-5 h-5 ${agent.color}`} />
                          <Switch checked={enabled} onCheckedChange={() => toggleAgent(agent.id)} />
                        </div>
                        <h3 className="font-semibold text-sm text-neutral-900">{agent.name}</h3>
                        <p className="text-xs text-neutral-500 mb-3">{agent.desc}</p>
                        <Button size="sm" variant="outline" className="w-full"
                          onClick={() => triggerAgent(agent.id)}
                          disabled={triggering === agent.id || !enabled}
                          data-testid={`trigger-${agent.id}`}>
                          {triggering === agent.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                          Run Now
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Stats */}
              {status?.stats && (
                <div className="grid grid-cols-5 gap-3 mb-6">
                  {[
                    { label: "Prospects", value: status.stats.total_prospects, icon: Users, color: "text-blue-500" },
                    { label: "Emails Sent", value: status.stats.outreach_sent, icon: Mail, color: "text-emerald-500" },
                    { label: "Replies", value: status.stats.replies, icon: Send, color: "text-green-500" },
                    { label: "Content", value: status.stats.content_generated, icon: FileText, color: "text-amber-500" },
                    { label: "Pipeline", value: status.stats.pipeline_deals, icon: Briefcase, color: "text-purple-500" },
                  ].map(s => (
                    <Card key={s.label} className="border-neutral-200 bg-neutral-50">
                      <CardContent className="p-3 text-center">
                        <s.icon className={`w-4 h-4 ${s.color} mx-auto mb-1`} />
                        <p className="text-xl font-bold text-neutral-900">{s.value}</p>
                        <p className="text-[10px] text-neutral-500">{s.label}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-5 mb-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="prospects">Prospects</TabsTrigger>
                  <TabsTrigger value="outreach">Outreach</TabsTrigger>
                  <TabsTrigger value="content">Content</TabsTrigger>
                  <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
                </TabsList>

                <TabsContent value="overview">
                  <h3 className="font-semibold text-neutral-900 mb-3">Agent Activity Log</h3>
                  {logs.length === 0 ? <p className="text-neutral-500 text-center py-6">No activity yet — trigger an agent to start</p> : (
                    <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                      {logs.map(log => (
                        <div key={log.log_id} className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg text-sm">
                          <Badge variant="outline" className="text-xs font-mono">{log.agent_id}</Badge>
                          <span className="flex-1 text-neutral-700">{log.message}</span>
                          <span className="text-xs text-neutral-400">{log.created_at?.slice(0, 16).replace("T", " ")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="prospects">
                  <h3 className="font-semibold text-neutral-900 mb-3">Generated Prospects ({prospects.length})</h3>
                  {prospects.length === 0 ? <p className="text-neutral-500 text-center py-6">No prospects yet</p> : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {prospects.map(p => (
                        <Card key={p.prospect_id} className="border-neutral-200">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm text-neutral-900">{p.company_name}</p>
                              <p className="text-xs text-neutral-600">{p.contact_name} — {p.contact_title}</p>
                              <p className="text-xs text-neutral-500">{p.industry} • {p.location}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <Badge className={p.status === "new" ? "bg-blue-100 text-blue-700" : p.status === "contacted" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>{p.status}</Badge>
                              <p className="text-xs text-neutral-400 mt-1">Score: {p.priority_score}/10</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="outreach">
                  <h3 className="font-semibold text-neutral-900 mb-3">Outreach Emails ({outreach.length})</h3>
                  {outreach.length === 0 ? <p className="text-neutral-500 text-center py-6">No outreach sent yet</p> : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {outreach.map(o => (
                        <Card key={o.outreach_id} className="border-neutral-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-1">
                              <p className="font-semibold text-sm text-neutral-900">{o.company} — {o.prospect_name}</p>
                              <Badge className="bg-emerald-100 text-emerald-700">Step {o.step}</Badge>
                            </div>
                            <p className="text-xs text-neutral-600 mb-1">{o.subject}</p>
                            <p className="text-xs text-neutral-400">{o.prospect_email} • {o.created_at?.slice(0, 10)}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="content">
                  <h3 className="font-semibold text-neutral-900 mb-3">Generated Content ({content.length})</h3>
                  {content.length === 0 ? <p className="text-neutral-500 text-center py-6">No content generated yet</p> : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {content.map(c => (
                        <Card key={c.content_id} className="border-neutral-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <Badge variant="outline" className="text-xs">{c.content_type?.replace(/_/g, " ")}</Badge>
                              <Button size="sm" variant="ghost" onClick={() => copyContent(c.content, c.content_id)}>
                                {copied === c.content_id ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                              </Button>
                            </div>
                            <p className="text-sm text-neutral-700 whitespace-pre-wrap line-clamp-4">{c.content}</p>
                            {c.hashtags && <p className="text-xs text-blue-500 mt-2">{c.hashtags.join(" ")}</p>}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="pipeline">
                  <h3 className="font-semibold text-neutral-900 mb-3">Sales Pipeline ({pipeline.length})</h3>
                  {pipeline.length === 0 ? <p className="text-neutral-500 text-center py-6">No deals in pipeline yet</p> : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {pipeline.map(d => (
                        <Card key={d.deal_id} className="border-neutral-200">
                          <CardContent className="p-4 flex items-center gap-4">
                            <div className="flex-1">
                              <p className="font-semibold text-sm text-neutral-900">{d.company}</p>
                              <p className="text-xs text-neutral-600">{d.contact} — {d.service}</p>
                            </div>
                            <Badge className={d.stage === "discovery" ? "bg-blue-100 text-blue-700" : d.stage === "proposal" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>
                              {d.stage}
                            </Badge>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SalesAgentsDashboard;
